/**
 * React hook for wallet onboarding and auth token generation
 * Single source of truth for all account-related information:
 * - Mnemonic phrase
 * - Wallet address (derived from mnemonic)
 * - Private key (computed from mnemonic, not stored)
 * - Auth tokens (authToken, refreshToken, expiresAt)
 *
 * Provides onboarding functionality with loading and error states
 * Persists account data to localStorage
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ethers } from "ethers";
import { onboardAndGetAuthToken } from "../sdk/utils/onboard";
import mormSigner from "../sdk/signing/mormSigner";

export interface UseOnboardResult {
  // Account information (single source of truth)
  mnemonic: string;
  walletAddress: string;
  privateKey: string; // Computed from mnemonic, null if mnemonic not available

  // Auth tokens
  authToken: string;
  refreshToken?: string;
  expiresAt?: number;

  // State
  loading: boolean;
  error: string | null;
  addressLoading: boolean; // Loading state for address generation

  // Methods
  onboard: (
    mnemonic: string,
    baseURL: string,
    username?: string,
    referralCode?: string,
  ) => Promise<void>;
  setMnemonic: (mnemonic: string) => Promise<void>; // Set mnemonic and generate address
  setAccountData: (mnemonic: string, address: string) => void; // Set both mnemonic and address directly
  clearAccountData: () => void; // Clear all account data
  clearError: () => void;
  reset: () => void; // Reset auth tokens only (keeps account data)
  resetAll: () => void; // Reset everything including account data
}

// localStorage keys
const STORAGE_KEY_MNEMONIC = "onboard_mnemonic";
const STORAGE_KEY_WALLET_ADDRESS = "onboard_wallet_address";
const STORAGE_KEY_AUTH_TOKEN = "onboard_auth_token";
const STORAGE_KEY_REFRESH_TOKEN = "onboard_refresh_token";
const STORAGE_KEY_EXPIRES_AT = "onboard_expires_at";

/**
 * Hook for onboarding wallet and managing account information
 * Single source of truth for mnemonic, wallet address, private key, and auth tokens
 * @param initialBaseURL - Optional initial base URL (can be overridden in onboard call)
 * @returns UseOnboardResult with account state and methods
 */
export function useOnboard(_initialBaseURL?: string): UseOnboardResult {
  // Account state
  const [mnemonic, setMnemonicState] = useState<string>("");
  const [walletAddress, setWalletAddress] = useState<string>("");
  const [addressLoading, setAddressLoading] = useState(false);

  // Auth token state
  const [authToken, setAuthToken] = useState<string>("");
  const [refreshToken, setRefreshToken] = useState<string | undefined>(
    undefined,
  );
  const [expiresAt, setExpiresAt] = useState<number | undefined>(undefined);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load persisted data from localStorage on mount
  useEffect(() => {
    const storedMnemonic = localStorage.getItem(STORAGE_KEY_MNEMONIC);
    const storedAddress = localStorage.getItem(STORAGE_KEY_WALLET_ADDRESS);
    const storedAuthToken = localStorage.getItem(STORAGE_KEY_AUTH_TOKEN);
    const storedRefreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN);
    const storedExpiresAt = localStorage.getItem(STORAGE_KEY_EXPIRES_AT);

    if (storedMnemonic) {
      setMnemonicState(storedMnemonic);
    } else {
      setMnemonicState("");
    }
    if (storedAddress) {
      setWalletAddress(storedAddress);
    } else {
      setWalletAddress("");
    }
    if (storedAuthToken) {
      setAuthToken(storedAuthToken);
    }
    if (storedRefreshToken) {
      setRefreshToken(storedRefreshToken);
    }
    if (storedExpiresAt) {
      const expiresAtNum = parseInt(storedExpiresAt, 10);
      if (!isNaN(expiresAtNum)) {
        setExpiresAt(expiresAtNum);
      }
    }
  }, []);

  // Generate wallet address from mnemonic
  const generateAddressFromMnemonic = useCallback(
    async (mnemonicValue: string): Promise<string | null> => {
      if (!mnemonicValue || mnemonicValue.trim() === "") {
        return null;
      }

      setAddressLoading(true);
      try {
        // Initialize mormSigner if not ready
        if (!mormSigner.isReady()) {
          await mormSigner.initialize();
        }

        // Generate address from mnemonic
        const result = await mormSigner.generateAddress(
          mnemonicValue.trim(),
          0,
        );

        if (result.success && result.address) {
          return result.address;
        } else {
          console.error("Failed to generate address:", result.error);
          return null;
        }
      } catch (err: any) {
        console.error("Error generating address:", err);
        return null;
      } finally {
        setAddressLoading(false);
      }
    },
    [],
  );

  // Set mnemonic and generate address
  const setMnemonic = useCallback(
    async (mnemonicValue: string) => {
      const trimmedMnemonic = mnemonicValue.trim();

      if (trimmedMnemonic === "") {
        // Clear account data if empty mnemonic
        setMnemonicState("");
        setWalletAddress("");
        localStorage.removeItem(STORAGE_KEY_MNEMONIC);
        localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
        return;
      }

      setMnemonicState(trimmedMnemonic);
      localStorage.setItem(STORAGE_KEY_MNEMONIC, trimmedMnemonic);

      // Generate address from mnemonic
      const address = await generateAddressFromMnemonic(trimmedMnemonic);
      if (address) {
        setWalletAddress(address);
        localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, address);
      } else {
        // Clear address if generation failed
        setWalletAddress("");
        localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
      }
    },
    [generateAddressFromMnemonic],
  );

  // Set both mnemonic and address directly (useful when address is already known)
  const setAccountData = useCallback(
    (mnemonicValue: string, address: string) => {
      const trimmedMnemonic = mnemonicValue.trim();
      const trimmedAddress = address.trim().toLowerCase();

      setMnemonicState(trimmedMnemonic);
      setWalletAddress(trimmedAddress);
      localStorage.setItem(STORAGE_KEY_MNEMONIC, trimmedMnemonic);
      localStorage.setItem(STORAGE_KEY_WALLET_ADDRESS, trimmedAddress);
    },
    [],
  );

  // Clear all account data
  const clearAccountData = useCallback(() => {
    setMnemonicState("");
    setWalletAddress("");
    localStorage.removeItem(STORAGE_KEY_MNEMONIC);
    localStorage.removeItem(STORAGE_KEY_WALLET_ADDRESS);
  }, []);

  // Compute private key from mnemonic (not stored, computed on demand)
  const privateKey = useMemo((): string => {
    if (mnemonic === "") {
      return "";
    }
    const trimmedMnemonic = mnemonic.trim();
    if (trimmedMnemonic === "") {
      return "";
    }

    try {
      // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
      // This matches the same derivation used in onboard.ts and FiClient
      const masterNode = ethers.HDNodeWallet.fromPhrase(
        trimmedMnemonic,
        "", // no passphrase
        "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
      );

      // Validate that derived address matches stored address if available
      if (
        walletAddress &&
        masterNode.address.toLowerCase() !== walletAddress.toLowerCase()
      ) {
        console.warn(
          "Address mismatch: derived address does not match stored address",
        );
        return "";
      }

      // Extract private key in the exact format Go's crypto.ToECDSA expects
      const privateKeyBigInt = BigInt(masterNode.privateKey);
      const SECP256K1_ORDER = BigInt(
        "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
      );

      if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
        console.error("Invalid private key: out of secp256k1 curve order");
        return "";
      }

      // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
      const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
      const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

      if (!/^[0-9a-f]{64}$/.test(paddedHex)) {
        console.error("Invalid private key format");
        return "";
      }

      return paddedHex;
    } catch (err: any) {
      console.error("Error computing private key:", err);
      return "";
    }
  }, [mnemonic, walletAddress]);

  // Onboard with mnemonic
  const onboard = useCallback(
    async (
      mnemonicValue: string,
      baseURL: string,
      username?: string,
      referralCode?: string,
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        // First, set the mnemonic and generate address
        await setMnemonic(mnemonicValue);

        // Then perform onboarding
        const result = await onboardAndGetAuthToken(
          mnemonicValue,
          baseURL,
          username,
          referralCode,
        );

        if (result.success && result.authToken) {
          setAuthToken(result.authToken);
          setRefreshToken(result.refreshToken);
          setExpiresAt(result.expiresAt);

          // Persist auth tokens to localStorage
          localStorage.setItem(STORAGE_KEY_AUTH_TOKEN, result.authToken);
          if (result.refreshToken) {
            localStorage.setItem(
              STORAGE_KEY_REFRESH_TOKEN,
              result.refreshToken,
            );
          }
          if (result.expiresAt) {
            localStorage.setItem(
              STORAGE_KEY_EXPIRES_AT,
              result.expiresAt.toString(),
            );
          }

          toast.success("Onboarding successful", {
            description: "Wallet authenticated and auth token generated.",
            duration: 5000,
          });
        } else {
          const errorMsg = result.error || "Onboarding failed";
          setError(errorMsg);
          setAuthToken("");
          setRefreshToken(undefined);
          setExpiresAt(undefined);
          localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
          toast.error("Onboarding failed", {
            description: errorMsg,
            duration: 6000,
          });
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        const errorMsg = err?.message || "Unknown error during onboarding";
        setError(errorMsg);
        setAuthToken("");
        setRefreshToken(undefined);
        setExpiresAt(undefined);
        localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
        toast.error("Onboarding error", {
          description: errorMsg,
          duration: 6000,
        });
        throw err instanceof Error ? err : new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [setMnemonic],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset auth tokens only (keeps account data)
  const reset = useCallback(() => {
    setAuthToken("");
    setRefreshToken(undefined);
    setExpiresAt(undefined);
    setError(null);
    setLoading(false);
    localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEY_EXPIRES_AT);
  }, []);

  // Reset everything including account data
  const resetAll = useCallback(() => {
    clearAccountData();
    reset();
  }, [clearAccountData, reset]);

  return {
    // Account information
    mnemonic,
    walletAddress,
    privateKey,

    // Auth tokens
    authToken,
    refreshToken,
    expiresAt,

    // State
    loading,
    error,
    addressLoading,

    // Methods
    onboard,
    setMnemonic,
    setAccountData,
    clearAccountData,
    clearError,
    reset,
    resetAll,
  };
}
